// Client-side re-ranker (BUILD_PLAN.md §6 Phase 4, task step 1).
//
//   personalized_score = entry.score
//                      + lambda * dot(user_vec, article_tags)
//                      + recency_weight   * recency_boost(age, half_life)
//                      + velocity_weight  * entry.engagement_velocity_score
//                      + authority_weight * entry.source_authority_score
//
// Then a two-pass diversity penalty (publisherDiversity + topicDiversity)
// nudges consecutive articles from the same publisher or sharing a top
// tag down the list.
//
// `entry.score` is the server-computed composite. The component
// breakdown (recency / authority / velocity / novelty) is also exposed
// on each ManifestEntry so the radar in the Settings drawer can show
// the user's profile across the same six axes the formula uses.
//
// This module is intentionally pure (no IndexedDB, no network): it is
// verified against synthetic interest vectors before any events flow.

import type { InterestVector } from "./interest";
import type { ManifestEntry } from "./types";

export interface RankParams {
  /** Weight on the user-vector ⋅ article-tags affinity term ("Match"). */
  lambda: number;
  /** Weight on the recency boost term ("Freshness"). */
  recencyWeight: number;
  /** Half-life (hours) controlling the recency-boost decay slope. */
  recencyHalfLifeHours: number;
  /** Weight on `entry.engagement_velocity_score` ("Trending"). 0 = ignore. */
  velocityWeight: number;
  /** Weight on `entry.source_authority_score` ("Authority"). 0 = ignore. */
  authorityWeight: number;
  /** Strength of the post-sort penalty for consecutive articles from the
   *  same publisher ("Variety"). 0 = no penalty; 1 = same-publisher
   *  candidates lose a full score-unit relative to a different-
   *  publisher alternative, which is enough to demote them past any
   *  candidate with a positive score in the normalized [0, 1] range.
   *  The penalty is a constant ADDITIVE deduction (not proportional
   *  to the candidate's own score) so the intra-group order — i.e.
   *  among same-publisher candidates when no alternative remains —
   *  stays score-sorted. Soft reset: the penalty is per-slot and only
   *  against the immediately preceding placed article, so X-Y-X is
   *  the intended outcome rather than a permanent ban on X. */
  publisherDiversity: number;
  /** Same shape as publisherDiversity but keyed on the article's first
   *  tag (its "primary topic" by enrich's convention). 0 = no penalty;
   *  1 = same-top-tag candidates lose a full score-unit relative to a
   *  different-tag alternative ("Discovery"). */
  topicDiversity: number;
}

export const DEFAULT_RANK_PARAMS: RankParams = {
  lambda: 0.5,
  recencyWeight: 0.2,
  recencyHalfLifeHours: 24,
  velocityWeight: 0.0,
  authorityWeight: 0.1,
  publisherDiversity: 0.0,
  topicDiversity: 0.0,
};

/** `dot(user_vec, article_tags)` with unit-weighted tags: the sum of the
 * user's interest weight for each tag present on the article. */
export function tagAffinity(
  vec: InterestVector,
  tags: readonly string[] | undefined,
): number {
  if (!tags || tags.length === 0) return 0;
  let sum = 0;
  for (const t of tags) {
    const w = vec[t];
    if (w) sum += w;
  }
  return sum;
}

/** Exponential recency boost in `[0, 1]`: 1.0 at `nowMs`, 0.5 one half-life
 * later. Future timestamps are treated as "now". */
export function recencyBoost(
  publishedAtMs: number,
  nowMs: number,
  halfLifeHours: number,
): number {
  const ageMs = Math.max(0, nowMs - publishedAtMs);
  const halfLifeMs = halfLifeHours * 3_600_000;
  if (halfLifeMs <= 0) return 1;
  return 0.5 ** (ageMs / halfLifeMs);
}

/** Server-side novelty contribution constant. The PWA doesn't expose a
 *  novelty slider (six axes feels like the right ceiling for a hexagonal
 *  radar), so when we re-compose the score client-side we want some
 *  weight on novelty so deduped / surprising articles aren't ignored.
 *  Matches the server's default `ScoreWeights.novelty` so the no-slider-
 *  movement case approximates the server composite. */
const NOVELTY_BASELINE = 0.15;

/** Does this manifest entry carry the per-component score breakdown
 *  added in PR #75? Older manifests cached in the SW will have only
 *  `entry.score`; we fall back to additive-on-top math in that case to
 *  stay backward-compatible. */
function hasComponents(entry: ManifestEntry): boolean {
  return (
    entry.recency_score !== undefined ||
    entry.source_authority_score !== undefined ||
    entry.engagement_velocity_score !== undefined ||
    entry.novelty_score !== undefined
  );
}

export function personalizedScore(
  entry: ManifestEntry,
  vec: InterestVector,
  params: RankParams = DEFAULT_RANK_PARAMS,
  nowMs: number = Date.now(),
): number {
  const affinity = params.lambda * tagAffinity(vec, entry.tags);
  const freshness =
    params.recencyWeight *
    recencyBoost(entry.published_at, nowMs, params.recencyHalfLifeHours);

  if (hasComponents(entry)) {
    // Slider value IS the weight. We rebuild the composite client-side
    // from the four exposed component scores so dragging "Trending" to
    // 1.0 means "engagement velocity is THE dominant axis", not "add
    // 1.0 on top of the server's 0.30". Novelty has no slider yet;
    // carry it at a small fixed baseline so dedup wins stay in play.
    return (
      params.velocityWeight * (entry.engagement_velocity_score ?? 0) +
      params.authorityWeight * (entry.source_authority_score ?? 0) +
      NOVELTY_BASELINE * (entry.novelty_score ?? 0) +
      freshness +
      affinity
    );
  }

  // Legacy / pre-PR-#75 manifest path: components weren't in the wire
  // format, so we can't recompose. Stay backward-compatible by using
  // the server's pre-baked composite as the base and adding the
  // personalized terms on top. Note the trade-off the reviewer flagged:
  // here the sliders read as "additive nudge", not "the weight."
  return entry.score + affinity + freshness;
}

interface Scored {
  entry: ManifestEntry;
  index: number;
  s: number;
}

/** Greedy post-sort diversity penalty: walk the sorted list head-first,
 *  and at each position prefer items whose publisher/top-tag aren't the
 *  same as the immediately preceding placed item. Concretely, for the
 *  next slot we score every remaining candidate as `s - penalty(prev)`
 *  and pick the highest. With `publisherDiversity = 0` and
 *  `topicDiversity = 0` this is a no-op (penalty=0 for all candidates),
 *  so the existing pure-score sort is preserved.
 *
 *  The penalty is intentionally per-slot (not cumulative across the
 *  whole list): two articles from publisher X spaced by an article from
 *  publisher Y is the desired result, not a permanent ban.
 *
 *  Complexity: O(N²). At N=200 (the manifest cap per
 *  `crates/publish/src/manifest.rs::MANIFEST_SIZE`) that's ~40k cmp
 *  ops per re-rank, well inside a slider-drag frame budget on every
 *  device we ship to. If MANIFEST_SIZE is ever raised past ~500 this
 *  should be reconsidered — likely by short-circuiting to a plain
 *  sort when N exceeds a guard, or by limiting the diversity pass to
 *  the top-K visible window. */
function applyDiversityPass(
  sorted: Scored[],
  params: RankParams,
): Scored[] {
  if (params.publisherDiversity <= 0 && params.topicDiversity <= 0) {
    return sorted;
  }
  const result: Scored[] = [];
  const pool = sorted.slice();
  while (pool.length > 0) {
    const prev = result[result.length - 1]?.entry;
    let bestIdx = 0;
    let bestPenalized = -Infinity;
    for (let i = 0; i < pool.length; i += 1) {
      const cand = pool[i];
      let penalty = 0;
      if (prev) {
        if (
          params.publisherDiversity > 0 &&
          prev.publisher &&
          cand.entry.publisher &&
          prev.publisher === cand.entry.publisher
        ) {
          // Constant additive penalty (not score-proportional). With a
          // score-proportional penalty, the all-same-publisher
          // degenerate case equalizes every remaining candidate at
          // ~zero adjusted score, and the tiebreak silently flips the
          // intra-group order. A constant penalty (1.0 reference) is
          // large enough to push same-publisher candidates well below
          // any different-publisher alternative when one exists, while
          // preserving the score-based order WITHIN the same-publisher
          // group when no alternative is available.
          penalty += params.publisherDiversity;
        }
        if (
          params.topicDiversity > 0 &&
          prev.tags &&
          prev.tags.length > 0 &&
          cand.entry.tags &&
          cand.entry.tags.length > 0 &&
          prev.tags[0] === cand.entry.tags[0]
        ) {
          penalty += params.topicDiversity;
        }
      }
      const adjusted = cand.s - penalty;
      if (
        adjusted > bestPenalized ||
        (adjusted === bestPenalized && cand.index < pool[bestIdx].index)
      ) {
        bestPenalized = adjusted;
        bestIdx = i;
      }
    }
    result.push(pool[bestIdx]);
    pool.splice(bestIdx, 1);
  }
  return result;
}

/** Re-rank a manifest against the interest vector, highest score first.
 * Stable: ties preserve the manifest's original (base-score) order.
 * When `publisherDiversity` or `topicDiversity` are non-zero, the sort
 * is followed by a greedy diversity pass (see `applyDiversityPass`). */
export function rankArticles(
  entries: readonly ManifestEntry[],
  vec: InterestVector,
  params: RankParams = DEFAULT_RANK_PARAMS,
  nowMs: number = Date.now(),
): ManifestEntry[] {
  const scored: Scored[] = entries.map((entry, index) => ({
    entry,
    index,
    s: personalizedScore(entry, vec, params, nowMs),
  }));
  scored.sort((a, b) => b.s - a.s || a.index - b.index);
  return applyDiversityPass(scored, params).map((x) => x.entry);
}
